import styled from '@emotion/styled'
import { isMobile } from '@const/device'
import Title from '@organisms/Title'
import Subtitle from '@organisms/Subtitle'

export const StyledSubtitle = styled(Subtitle)`
  margin-bottom: ${({ theme }) => theme.sizes.twoBase};
`

export const ProgressWrapper = styled.div<{ withBottomMargin?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 15px;
  gap: 10px;
`

export const Footer = styled.div`
  display: flex;
  margin-top: ${({ theme }) => theme.sizes.threeBase};
  flex-direction: ${() => (isMobile ? 'column' : 'row')};
  gap: 10px;
`

export const StyledTitle = styled(Title)`
  white-space: pre-wrap;
  margin-bottom: 15px;
`

export const CourseCompletionModalContent = styled.div`
  display: flex;
  align-items: flex-start;
  min-height: 265px;
  padding: 23px 53px 42px 23px;
  gap: 48px;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: center;
    min-height: auto;
    padding: 44px 18px 24px;
    gap: 20px;
    text-align: center;
  }
`

export const CourseCompletionBear = styled.img`
  width: 200px;
  height: 200px;
  flex: 0 0 200px;
  object-fit: cover;
  border-radius: 50%;

  @media (max-width: 720px) {
    width: 160px;
    height: 160px;
    flex-basis: 160px;
  }
`

export const CourseCompletionBody = styled.div<{
  $isCoursePassedCompletely?: boolean
}>`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  padding-top: ${({ $isCoursePassedCompletely }) =>
    $isCoursePassedCompletely ? '39px' : '14px'};

  @media (max-width: 720px) {
    align-items: center;
    padding-top: 0;
  }
`

export const CourseCompletionTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.black};
  font-size: 36px;
  line-height: 43px;
  font-weight: 700;
  letter-spacing: 0;

  @media (max-width: 720px) {
    font-size: 26px;
    line-height: 32px;
  }
`

export const CourseCompletionDescription = styled.p`
  margin: 20px 0 0;
  color: ${({ theme }) => theme.colors.text.black};
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  letter-spacing: 0;

  @media (max-width: 720px) {
    margin-top: 14px;
    font-size: 14px;
    line-height: 20px;
  }
`

export const CourseCompletionActions = styled.div<{
  $isCoursePassedCompletely?: boolean
}>`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: ${({ $isCoursePassedCompletely }) =>
    $isCoursePassedCompletely ? '19px' : '27px'};

  && button {
    width: auto;
    min-width: 132px;
    height: 58px;
    justify-content: center;
    border-radius: 30px;
    padding: 0 26px;
    background: #1d4e89;
    border: 1px solid #1d4e89;
    color: #ffffff;
    font-size: 16px;
    line-height: 19px;
    font-weight: 400;
    letter-spacing: 0.1em;
  }

  ${({ $isCoursePassedCompletely }) =>
    !$isCoursePassedCompletely
      ? `
        && > .ant-spin-nested-loading:last-of-type button {
          background: transparent;
          border-color: #1d4e89;
          color: #222222;
        }

        && > .ant-spin-nested-loading:last-of-type button:hover {
          background: #1d4e89;
          color: #ffffff;
        }
      `
      : ''}

  @media (max-width: 720px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    margin-top: 22px;

    && button {
      width: 100%;
      min-width: auto;
    }
  }
`

export const FinalResultPage = styled.div`
  width: 100%;
  max-width: 1340px;
`

export const FinalTopRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 17px;

  button {
    width: auto;
    min-width: 155px;
    height: 42px;
    padding: 0 17px;
    border-color: #1d4e89;
    color: #222222;
    background: transparent;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
    letter-spacing: 0.08em;
  }

  @media (max-width: 720px) {
    margin-bottom: ${({ theme }) => theme.sizes.twoBase};
  }
`

export const FinalResultCourseTitle = styled.h1`
  margin: 0 0 55px;
  color: ${({ theme }) => theme.colors.text.black};
  font-size: 28px;
  line-height: 34px;
  font-weight: 700;
  letter-spacing: 0;

  @media (max-width: 720px) {
    margin-bottom: ${({ theme }) => theme.sizes.threeBase};
    font-size: 24px;
    line-height: 30px;
  }
`

export const FinalResultContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 52px;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.sizes.twoBase};
    text-align: center;
  }
`

export const FinalResultBear = styled.img`
  width: 200px;
  height: 200px;
  flex: 0 0 200px;
  object-fit: cover;
  border-radius: 50%;
  align-self: flex-start;

  @media (max-width: 720px) {
    width: 160px;
    height: 160px;
    flex-basis: 160px;
    align-self: center;
  }
`

export const FinalResultBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 29px;
  flex: 1;
  min-width: 0;
  padding-top: 1px;

  @media (max-width: 720px) {
    align-items: stretch;
    gap: ${({ theme }) => theme.sizes.twoBase};
    padding-top: 0;
  }
`

export const FinalResultPanel = styled.div<{ isSuccess: boolean }>`
  position: relative;
  width: 100%;
  min-height: 486px;
  border-radius: 30px;
  padding: 51px 80px 50px 52px;
  background: ${({ isSuccess }) =>
    isSuccess ? '#ebffe9' : '#fee7e9'};

  @media (max-width: 720px) {
    border-radius: 22px;
    min-height: auto;
    padding: 34px 20px 28px;
  }
`

export const FinalResultHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 720px) {
    gap: 10px;
  }
`

export const FinalResultTitle = styled.h2`
  margin: 0;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.black};
  font-size: 36px;
  line-height: 43px;
  letter-spacing: 0;

  @media (max-width: 720px) {
    font-size: 24px;
    line-height: 30px;
    text-align: center;
  }
`

export const FinalPageStatus = styled.div<{ isSuccess: boolean }>`
  margin: 0;
  color: #222222;
  font-size: 18px;
  line-height: 22px;
  font-weight: 400;

  @media (max-width: 720px) {
    font-size: 16px;
    line-height: 22px;
    text-align: center;
  }
`

export const FinalResultRows = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 9px;

  @media (max-width: 720px) {
    align-items: stretch;
  }
`

export const FinalResultRow = styled.div`
  margin: 0;
  display: inline-flex;
  align-items: center;
  width: fit-content;
  min-height: 51px;
  color: #222222;
  background: #ffffff;
  border-radius: 15px;
  padding: 14px 14px;
  font-size: 18px;
  line-height: 22px;
  font-weight: 400;

  > span {
    margin-right: 5px;
  }

  @media (max-width: 720px) {
    font-size: 16px;
    line-height: 22px;
    padding: 14px 16px;
    width: 100%;
    justify-content: center;
    text-align: center;
  }
`

export const FinalActions = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 2px;

  && button {
    width: auto;
    min-width: 150px;
    height: 62px;
    border-radius: 30px;
    padding: 0 28px;
    justify-content: center;
    font-size: 16px;
    line-height: 19px;
    font-weight: 400;
    letter-spacing: 0.1em;
  }

  && > .ant-spin-nested-loading:first-of-type button {
    min-width: 333px;
    background: #1d4e89;
    border-color: #1d4e89;
    color: #ffffff;
  }

  && > .ant-spin-nested-loading:first-of-type button::before {
    content: '↻';
    font-size: 20px;
    line-height: 1;
    letter-spacing: 0;
  }

  && > .ant-spin-nested-loading:last-of-type button {
    min-width: 148px;
    background: transparent;
    border-color: #1d4e89;
    color: #222222;
  }

  @media (max-width: 720px) {
    gap: 12px;

    && button {
      min-width: auto;
      width: 100%;
    }

    flex-direction: column;
  }
`
